import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { 
  Users, 
  FileText, 
  Eye, 
  TrendingUp,
  Calendar,
  Clock,
  Bell,
  Edit3,
  MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const leadSourceNames = {
  'iniciacio-a-soldeu': 'Landing Soldeu',
  'curs-iniciacio-golf': 'Landing Curs Iniciació',
  'floating-button': 'Botó flotant WhatsApp',
  'academia-nuts': 'Acadèmia NUTS',
  'programa-nuts-privada': 'NUTS · Sessió privada',
  'programa-nuts-grup': 'NUTS · Sessió grup',
  'programa-nuts-telefon': 'NUTS · Telèfon',
  'programa-nuts-footer': 'NUTS · Footer',
  'contacte': 'Pàgina Contacte',
  'fes-te-abonat': 'Fes-te Abonat'
};

// Page names for display
const pageNames = {
  "home": "Inici",
  "el-camp": "El Camp",
  "jugar": "Jugar",
  "competicio": "Competició",
  "iniciacio": "Acadèmia",
  "driving-range": "Pràctiques",
  "restaurant": "Restaurant",
  "allotjament": "Allotjament",
  "correspondencies": "Correspondències",
  "contacte": "Contacte",
  "equip": "L'Equip"
};

// Language names
const langNames = {
  "ca": "CA",
  "es": "ES",
  "fr": "FR",
  "en": "EN"
};

export const AdminDashboard = () => {
  const { user, isAdmin, authFetch } = useAuth();
  const [stats, setStats] = useState({
    users: 0,
    pages: 0,
    tournaments: 0,
    contacts: 0
  });
  const [recentChanges, setRecentChanges] = useState([]);
  const [leadStats, setLeadStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch tournaments
        const tournamentsRes = await authFetch(`${API_URL}/api/tournaments`, {  });
        if (tournamentsRes.ok) {
          const tournaments = await tournamentsRes.json();
          setStats(prev => ({
            ...prev,
            tournaments: (tournaments.past?.length || 0) + (tournaments.future?.length || 0)
          }));
        }
        
        // Fetch WhatsApp lead stats
        const leadsRes = await authFetch(`${API_URL}/api/admin/leads/stats`, {  });
        if (leadsRes.ok) {
          setLeadStats(await leadsRes.json());
        }
        
        // Fetch recent changes (admin only)
        if (isAdmin) {
          const changesRes = await authFetch(`${API_URL}/api/admin/changes?limit=10`, {  });
          if (changesRes.ok) {
            const data = await changesRes.json();
            setRecentChanges(data.changes || []);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [isAdmin]);

  const quickActions = [
    { 
      label: 'Editar pàgines', 
      description: 'Modifica el contingut de les pàgines',
      href: '/admin/pages',
      icon: FileText,
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      label: 'Gestionar torneigs', 
      description: 'Afegeix o edita torneigs',
      href: '/admin/tournaments',
      icon: Calendar,
      color: 'bg-green-50 text-green-600'
    }
  ];

  if (isAdmin) {
    quickActions.push({
      label: 'Gestionar usuaris',
      description: 'Administra els usuaris del CMS',
      href: '/admin/users',
      icon: Users,
      color: 'bg-purple-50 text-purple-600'
    });
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ara mateix';
    if (diffMins < 60) return `Fa ${diffMins} min`;
    if (diffHours < 24) return `Fa ${diffHours}h`;
    if (diffDays < 7) return `Fa ${diffDays}d`;
    return date.toLocaleDateString('ca-ES', { day: '2-digit', month: '2-digit' });
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Welcome header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-display font-semibold text-[#1A4D2E]">
            Hola, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#666666] mt-1">
            Benvingut al panell d'administració d'UGOLF ARAVELL Andorra
          </p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#888888]">Torneigs</p>
                <p className="text-2xl font-semibold text-[#333333] mt-1">{stats.tournaments}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#888888]">Pàgines</p>
                <p className="text-2xl font-semibold text-[#333333] mt-1">11</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#888888]">Idiomes</p>
                <p className="text-2xl font-semibold text-[#333333] mt-1">4</p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#888888]">Forats</p>
                <p className="text-2xl font-semibold text-[#333333] mt-1">9</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp Leads */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="leads-stats-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#25D366]/10 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#25D366]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#333333]">Leads WhatsApp</h2>
              <p className="text-xs text-[#888888]">Clics als botons de WhatsApp per canal</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-semibold text-[#1A4D2E]" data-testid="leads-total">{leadStats?.total ?? 0}</p>
              <p className="text-xs text-[#888888]">total</p>
            </div>
          </div>
          {leadStats && leadStats.by_source.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[#888888] border-b border-[#E5E5E5]">
                    <th className="py-2 pr-4 font-medium">Canal</th>
                    <th className="py-2 pr-4 font-medium text-right">Últims 7 dies</th>
                    <th className="py-2 pr-4 font-medium text-right">Últims 30 dies</th>
                    <th className="py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {leadStats.by_source.map((row) => (
                    <tr key={row.source} className="border-b border-[#F0F0F0] last:border-0">
                      <td className="py-2 pr-4 text-[#333333]">{leadSourceNames[row.source] || row.source}</td>
                      <td className="py-2 pr-4 text-right text-[#666666]">{row.last7}</td>
                      <td className="py-2 pr-4 text-right text-[#666666]">{row.last30}</td>
                      <td className="py-2 text-right font-semibold text-[#1A4D2E]">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-[#888888]" data-testid="leads-empty">Encara no s'ha registrat cap clic. Els clics als botons de WhatsApp del web apareixeran aquí.</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick actions */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-[#333333] mb-4">Accions ràpides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickActions.map((action, index) => (
                <a
                  key={index}
                  href={action.href}
                  className="bg-white p-6 border border-[#E5E5E5] rounded-lg hover:border-[#1A4D2E] hover:shadow-sm transition-all group"
                  data-testid={`quick-action-${index}`}
                >
                  <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mb-4`}>
                    <action.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-medium text-[#333333] group-hover:text-[#1A4D2E]">
                    {action.label}
                  </h3>
                  <p className="text-sm text-[#888888] mt-1">
                    {action.description}
                  </p>
                </a>
              ))}
            </div>
          </div>

          {/* Recent changes (Admin only) */}
          {isAdmin && (
            <div>
              <h2 className="text-lg font-semibold text-[#333333] mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Canvis recents
              </h2>
              <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
                {recentChanges.length === 0 ? (
                  <div className="p-6 text-center text-[#888888] text-sm">
                    No hi ha canvis recents
                  </div>
                ) : (
                  <div className="divide-y divide-[#E5E5E5]">
                    {recentChanges.map((change, index) => (
                      <a
                        key={index}
                        href={`/admin/pages/${change.page_key}/${change.language}`}
                        className="block p-4 hover:bg-[#F9F9F7] transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Edit3 className="w-4 h-4 text-[#1A4D2E]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#333333] truncate">
                              {pageNames[change.page_key] || change.page_key}
                              <span className="ml-2 text-xs font-normal text-[#888888] bg-[#F5F5F5] px-1.5 py-0.5 rounded">
                                {langNames[change.language] || change.language}
                              </span>
                            </p>
                            <p className="text-xs text-[#888888] truncate">
                              {change.user_name}
                              {change.user_role === 'editor' && (
                                <span className="ml-1 text-amber-600">(Editor)</span>
                              )}
                            </p>
                          </div>
                          <span className="text-xs text-[#AAAAAA] flex-shrink-0">
                            {formatDate(change.timestamp)}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Role info */}
        <div className="bg-[#1A4D2E]/5 border border-[#1A4D2E]/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-[#1A4D2E] rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
              {user?.role === 'admin' ? 'A' : 'E'}
            </div>
            <div>
              <p className="font-medium text-[#1A4D2E]">
                {user?.role === 'admin' ? 'Rol: Administrador' : 'Rol: Editor'}
              </p>
              <p className="text-sm text-[#666666] mt-1">
                {user?.role === 'admin' 
                  ? 'Tens accés complet a totes les funcionalitats del CMS. Rebràs notificacions per email quan els editors facin canvis.'
                  : 'Pots editar continguts, imatges i enllaços. No pots modificar estructura, heroes ni configuració.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
